import { Box, IconButton, Typography, Grid, Button, Alert } from '@mui/material';
import { keyframes } from '@mui/material/styles';
import { useEffect, useRef, useState } from 'react';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import CloseIcon from '@mui/icons-material/Close';
import AddAPhotoIcon from '@mui/icons-material/AddAPhoto';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CircularProgress from '@mui/material/CircularProgress';

import { inputs } from './styles/CommonStyles';
import { useTranslation } from 'react-i18next';
import { useAlert } from '../../../../AlertContext';
import { Machine } from '../../../../ts/Connections';
import { SpectreResponse } from '../../../../ts/SpectreResponse';

interface AttachedPhoto {
    id: string;
    previewUrl: string;
    name: string;
    uploadedFileName: string;
    file: File;
    isPending?: boolean;
}

interface RemovablePhoto {
    id: string;
    name: string;
    uploadedFileName: string;
}

interface PhotoAttachmentProps {
    machine: Machine;
    machineActive: boolean;
    setMachineActive: (active: boolean) => void;
    onAiScan: (files: File[]) => Promise<void> | void;
    aiLoading: boolean;
    aiScanEnabled: boolean;
    clearPhotosSignal: number;
}

export default function PhotoAttachment({ machine, machineActive, setMachineActive, onAiScan, aiLoading, aiScanEnabled, clearPhotosSignal }: PhotoAttachmentProps) {
    const [photos, setPhotos] = useState<AttachedPhoto[]>([]);
    const [isUploading, setIsUploading] = useState(false);
    const [removingPhotoId, setRemovingPhotoId] = useState<string | null>(null);
    const [hasPendingFiles, setHasPendingFiles] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const wasMachineActiveRef = useRef(false);
    const photosRef = useRef<AttachedPhoto[]>([]);
    const { t } = useTranslation();
    const { openAlert } = useAlert();

    const styles = {
        ...inputs,
        container: {
            backgroundColor: '#FFFFFF',
            border: '1px solid #D3D3DE',
            borderRadius: 1,
            mt: 1,
            p: 1.5,
            minHeight: '80px',
        },
        headerRow: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1,
        },
        title: {
            fontWeight: 'bold',
            color: '#4B4E65',
            fontSize: '0.875rem',
        },
        cameraButton: {
            backgroundColor: '#FFFFFF',
            color: '#00004B',
            border: '1px solid #ccc',
            '&:hover': {
                backgroundColor: '#F5F5F8',
            },
            width: 40,
            height: 40,
            borderRadius: 1,
        },
        photosGrid: {
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            mt: 1.5,
        },
        photoWrapper: {
            position: 'relative',
            width: 70,
            height: 70,
        },
        photo: {
            width: '100%',
            height: '100%',
            objectFit: 'cover' as const,
            borderRadius: 1,
            border: '1px solid #D3D3DE',
        },
        removeButton: {
            position: 'absolute',
            top: -8,
            right: -8,
            backgroundColor: '#494545',
            color: '#FFFFFF',
            padding: '2px',
            '&:hover': {
                backgroundColor: '#020000',
            },
            width: 20,
            height: 20,
        },
        removeIcon: {
            fontSize: 14,
        },
        emptyState: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#9E9E9E',
            py: 2,
        },
        emptyIcon: {
            fontSize: 32,
            mb: 0.5,
        },
        emptyText: {
            fontSize: '0.75rem',
        },
        pendingWarning: {
            color: '#0288d1',
            width: 'fit-content',
            mx: 'auto',
        },
        loadingPlaceholder: {
            width: 70,
            height: 70,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 1,
            border: '1px solid #D3D3DE',
            backgroundColor: '#F5F5F8',
        },
    };

    const createPhotoId = () => {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
            return crypto.randomUUID();
        }

        return `photo-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
    };

    const isThumbnailDisplayableFile = (filePath: string) => {
        const normalizedFilePath = filePath.trim().toLowerCase().split('?')[0];
        return !normalizedFilePath.endsWith('.json');
    };

    const sanitizeFileName = (fileName: string) => fileName.replace(/ /g, '_');

    const uploadPhoto = async (file: File): Promise<AttachedPhoto> => {
        const sanitizedName = sanitizeFileName(file.name);
        const formData = new FormData();
        formData.append('file', file, sanitizedName);

        const response = await machine.connection.postForm('AttachFile', formData) as SpectreResponse;
        const attachFileResponse = response.Responses?.AttachFile;
        const uploadedFileName = attachFileResponse?.Path?.[0];

        if (attachFileResponse?.code !== '0' || !uploadedFileName) {
            const code = attachFileResponse?.code ?? 'unknown';
            const description =
                (typeof attachFileResponse?.description === 'string' && attachFileResponse.description) ||
                (typeof response.error === 'string' && response.error) ||
                (!uploadedFileName ? 'missing uploaded file path' : 'unknown error');
            throw new Error(`AttachFile failed for ${sanitizedName} (code ${code}): ${description}`);
        }

        return {
            id: createPhotoId(),
            previewUrl: URL.createObjectURL(file),
            name: sanitizedName,
            uploadedFileName,
            file,
        };
    };

    const handleCameraClick = () => {
        if (isUploading) return;
        fileInputRef.current?.click();
    };

    useEffect(() => {
        photosRef.current = photos;
    }, [photos]);

    useEffect(() => {
        if (!wasMachineActiveRef.current && machineActive) {
            setHasPendingFiles(false);
            setPhotos((prev) => prev.map((p) => ({ ...p, isPending: false })));
        }

        wasMachineActiveRef.current = machineActive;
    }, [machineActive]);

    // Clear photos only when we get a successful Dimension event signal
    useEffect(() => {
        if (clearPhotosSignal > 0) {
            setPhotos((prev) => {
                prev.forEach((photo) => URL.revokeObjectURL(photo.previewUrl));
                return [];
            });
        }
    }, [clearPhotosSignal]);

    // Sync isUploading and aiLoading with machineActive to prevent scanning during photo operations
    useEffect(() => {
        setMachineActive(isUploading || aiLoading);
    }, [isUploading, aiLoading, setMachineActive]);

    useEffect(() => {
        return () => {
            photosRef.current.forEach((photo) => URL.revokeObjectURL(photo.previewUrl));
        };
    }, []);

    useEffect(() => {
        let isMounted = true;

        const resolvePendingDirectory = (tag?: string) => {
            if (!tag) return 'nextscan';
            return `${tag.toLowerCase()}scan`;
        };

        const loadPendingFiles = async () => {
            try {
                const response = await machine.connection.get('ListPending', 5000) as SpectreResponse;
                const listPendingResponse = response.Responses?.ListPending;
                const pendingFiles = listPendingResponse?.Path;
                const pendingDirectory = resolvePendingDirectory(
                    typeof listPendingResponse?.Tag === 'string' ? listPendingResponse.Tag : undefined
                );

                if (!isMounted) return;

                if (!Array.isArray(pendingFiles) || pendingFiles.length === 0) {
                    setHasPendingFiles(false);
                    return;
                }

                const displayablePendingFiles = pendingFiles.filter((filePath) => isThumbnailDisplayableFile(filePath));

                if (displayablePendingFiles.length === 0) {
                    setHasPendingFiles(false);
                    return;
                }

                const photoItems = await Promise.all(
                    displayablePendingFiles.map(async (filePath) => {
                        // Extract just the filename from the full path (e.g., "persist/next/IMG_0204.jpeg" -> "IMG_0204.jpeg")
                        const fileName = filePath.split('/').pop() || filePath;
                        const fullFileUrl = await machine.connection.pendingFile(fileName, pendingDirectory);
                        const fileResponse = await fetch(fullFileUrl);
                        if (!fileResponse.ok) throw new Error(`Failed to fetch pending file ${fileName}: ${fileResponse.status}`);
                        const blob = await fileResponse.blob();
                        const file = new File([blob], fileName, { type: blob.type || 'image/jpeg' });

                        return {
                            id: createPhotoId(),
                            name: fileName,
                            uploadedFileName: filePath,
                            previewUrl: URL.createObjectURL(file), // Use local URL for consistency
                            file,
                            isPending: true,
                        };
                    })
                );

                if (!isMounted) return;
                // Revoke previous pending file URLs to prevent memory leaks
                // Also refreshes when switching machines to clear out old pending files that may no longer be relevant
                setPhotos((prev) => {
                    prev.filter((p) => p.isPending).forEach((p) => URL.revokeObjectURL(p.previewUrl));
                    const nonPending = prev.filter((p) => !p.isPending);
                    return [...nonPending, ...photoItems];
                });
                setHasPendingFiles(photoItems.length > 0);
            } catch {
                if (!isMounted) return;
                setHasPendingFiles(false);
            }
        };

        void loadPendingFiles();

        return () => {
            isMounted = false;
        };
    }, [machine]);

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files?.length) return;

        setIsUploading(true);

        const selectedFiles = Array.from(files);
        const uploadResults = await Promise.all(
            selectedFiles.map(async (file) => {
                try {
                    return {
                        fileName: file.name,
                        photo: await uploadPhoto(file),
                    };
                } catch (error) {
                    return {
                        fileName: file.name,
                        photo: null,
                        error: error instanceof Error ? error : new Error(String(error)),
                    };
                }
            })
        );

        const attachedPhotos = uploadResults
            .map((result) => result.photo)
            .filter((photo): photo is AttachedPhoto => photo !== null);

        if (attachedPhotos.length) {
            setPhotos((prev) => [...prev, ...attachedPhotos]);
        }

        const failedUploads = uploadResults.filter((result) => result.photo === null);

        if (failedUploads.length) {
            const firstError = failedUploads.find((result) => result.error)?.error;
            openAlert(
                t('photoAttachment.uploadFailed.title', 'Photo upload failed'),
                t(
                    'photoAttachment.uploadFailed.message',
                    `These files could not be attached: ${failedUploads.map((result) => result.fileName).join(', ')}`
                ),
                t('alertContext.ok', 'Ok'),
                undefined,
                false,
                firstError,
                {
                    component: 'PhotoAttachment',
                    action: 'AttachFile',
                    machineVersion: machine?.version ?? 'Unknown version',
                    connectionEndpoint: machine?.connection?.type?.() ?? 'Unknown connection',
                },
                'warning'
            );
        }

        setIsUploading(false);

        // Reset input so same file can be selected again
        event.target.value = '';
    };

    const handleRemovePhoto = async (photo: RemovablePhoto) => {
        if (isUploading) return;

        setIsUploading(true);
        setRemovingPhotoId(photo.id);

        try {
            const response = await machine.connection.post({ Requests: { RemoveAttachedFile: { Path: photo.uploadedFileName } } } as unknown as JSON) as SpectreResponse;
            const removeFileResponse = response.Responses?.RemoveAttachedFile;

            if (removeFileResponse?.code !== '0') {
                openAlert(
                    t('photoAttachment.removeFailed.title', 'Photo removal failed'),
                    t('photoAttachment.removeFailed.message', `Could not remove ${photo.name}.`),
                    t('alertContext.ok', 'Ok'),
                    undefined,
                    false,
                    undefined,
                    {
                        component: 'PhotoAttachment',
                        action: 'RemoveAttachedFile',
                        machineVersion: machine?.version ?? 'Unknown version',
                        connectionEndpoint: machine?.connection?.type?.() ?? 'Unknown connection',
                        fileName: photo.uploadedFileName,
                    },
                    'warning'
                );
                return;
            }

            setPhotos((prev) => {
                const removedPhoto = prev.find((item) => item.id === photo.id);

                if (removedPhoto) {
                    URL.revokeObjectURL(removedPhoto.previewUrl);
                }

                const nextPhotos = prev.filter((item) => item.id !== photo.id);
                setHasPendingFiles(nextPhotos.some((p) => p.isPending));
                return nextPhotos;
            });
        } catch (error) {
            const typedError = error instanceof Error ? error : new Error(String(error));
            openAlert(
                t('photoAttachment.removeFailed.title', 'Photo removal failed'),
                t('photoAttachment.removeFailed.message', `Could not remove ${photo.name}.`),
                t('alertContext.ok', 'Ok'),
                undefined,
                false,
                typedError,
                {
                    component: 'PhotoAttachment',
                    action: 'RemoveAttachedFile',
                    machineVersion: machine?.version ?? 'Unknown version',
                    connectionEndpoint: machine?.connection?.type?.() ?? 'Unknown connection',
                    fileName: photo.uploadedFileName,
                },
                'warning'
            );
        } finally {
            setIsUploading(false);
            setRemovingPhotoId(null);
        }
    };

    return (
        <Box sx={inputs.customFieldsBox}>
            <Grid
                container
                direction="row"
                justifyContent="space-evenly"
                alignItems="center"
            >
                <Grid item md={11} xs={11}>
                    <Box sx={styles.container}>
                        <Box sx={styles.headerRow}>
                            <Typography sx={styles.title}>
                                {t('photoAttachment.title', 'Photo Attachments')}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                {aiScanEnabled && photos.length > 0 && (
                                    <Button
                                        variant="outlined"
                                        startIcon={aiLoading ? (
                                            <CircularProgress size={16} sx={{ color: '#00004B' }} />
                                        ) : (
                                            <AutoAwesomeIcon
                                                sx={{
                                                    color: '#00004B',
                                                    '& .MuiSvgIcon-secondaryColor': { color: '#F7914B', opacity: 1 }
                                                }}
                                            />
                                        )}
                                        disabled={aiLoading || isUploading}
                                        onClick={() => {
                                            setMachineActive(true);
                                            void onAiScan(photos.map((photo) => photo.file));
                                        }}
                                        sx={{
                                            color: '#00004B',
                                            textTransform: 'none',
                                            height: 40,
                                            px: 2,
                                            fontWeight: 700,
                                            borderColor: '#ccc',
                                            '&:hover': {
                                                backgroundColor: '#F5F5F8',
                                                borderColor: '#ccc',
                                            },
                                        }}
                                    >
                                        {t('photoAttachment.detect', 'Detect')}
                                    </Button>
                                )}
                                <IconButton
                                    sx={styles.cameraButton}
                                    onClick={handleCameraClick}
                                    disabled={isUploading}
                                    aria-label={t('photoAttachment.addPhoto', 'Add photo') || 'Add photo'}
                                >
                                    <CameraAltIcon />
                                </IconButton>
                            </Box>
                        </Box>

                        {hasPendingFiles && (
                            <Alert sx={styles.pendingWarning} severity="info">{t('photoAttachment.pendingFilesWarning', 'Some files were found pending for the next scan')}</Alert>
                        )}

                        <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleFileChange}
                            accept="image/*"
                            capture="environment"
                            multiple
                            style={{ display: 'none' }}
                        />

                        {photos.length === 0 && !isUploading ? (
                            <Box sx={styles.emptyState}>
                                <AddAPhotoIcon sx={styles.emptyIcon} />
                                <Typography sx={styles.emptyText}>
                                    {t('photoAttachment.noPhotos', 'No photos attached')}
                                </Typography>
                            </Box>
                        ) : (
                            <Box sx={styles.photosGrid}>
                                {photos.map((photo) => (
                                    <Box key={photo.id} sx={styles.photoWrapper}>
                                        {removingPhotoId === photo.id ? (
                                            <Box sx={styles.loadingPlaceholder}>
                                                <CircularProgress size={24} />
                                            </Box>
                                        ) : (
                                            <>
                                                <img
                                                    src={photo.previewUrl}
                                                    alt={photo.name}
                                                    style={styles.photo}
                                                />
                                                <IconButton
                                                    sx={styles.removeButton}
                                                    onClick={() => void handleRemovePhoto(photo)}
                                                    disabled={isUploading}
                                                    aria-label={t('photoAttachment.removePhoto', 'Remove photo') || 'Remove photo'}
                                                    size="small"
                                                >
                                                    <CloseIcon sx={styles.removeIcon} />
                                                </IconButton>
                                            </>
                                        )}
                                    </Box>
                                ))}
                                {isUploading && !removingPhotoId && (
                                    <Box sx={styles.loadingPlaceholder}>
                                        <CircularProgress size={24} />
                                    </Box>
                                )}
                            </Box>
                        )}
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
}
